import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { securityInsightsApiRef } from '../api/apiRef';
import {
  CallerTeamsState,
  QuiesceServiceUnconfiguredError,
} from '../api/types';

/**
 * Fetches the caller's team memberships from `/whoami` exactly once per app
 * session, shared across all hook instances — mirrors the module-level-cache
 * idiom used by `useEntityExistenceTracker`. `attempted` (not `cachedTeams`)
 * is what gates re-fetching, so a transient failure doesn't cause every new
 * mount to retry — the session is stuck with that one outcome until reload,
 * same as a successful resolution would be.
 *
 * `teams` is `null` until the first attempt resolves, and stays `null` after
 * a transient failure (fail-open — callers should treat `null` as "don't
 * gate on this yet"). A resolved empty array (`[]`) is a real, cacheable
 * answer. `'unconfigured'` means the backend has no shared secret set up
 * yet — callers should hide the Quiesce/Lift affordance entirely rather
 * than fail open, since it can never succeed.
 */

let cachedTeams: CallerTeamsState = null;
let cachedLeadOfTeams: CallerTeamsState = null;
let cachedLeadOfExperiences: CallerTeamsState = null;
let attempted = false;
let inFlight: Promise<{
  teams: CallerTeamsState;
  isLeadOfTeams: CallerTeamsState;
  isLeadOfExperiences: CallerTeamsState;
}> | null = null;

/**
 * `isLeadOfTeams`/`isLeadOfExperiences` share the exact same fetch, cache,
 * and fail-open/'unconfigured' semantics as `teams` (see the doc comment
 * above) — they're additional fields on the same `/whoami` payload, not a
 * second network call. Backs the SLO commitment feature's client-side UX
 * gating (`utils/sloCommitmentOwnership.ts`) from the same identity data
 * the server actually authorizes writes against.
 */
export function useCallerIdentity(): {
  teams: CallerTeamsState;
  isLeadOfTeams: CallerTeamsState;
  isLeadOfExperiences: CallerTeamsState;
} {
  const api = useApi(securityInsightsApiRef);
  const [teams, setTeams] = useState<CallerTeamsState>(cachedTeams);
  const [isLeadOfTeams, setIsLeadOfTeams] = useState<CallerTeamsState>(
    cachedLeadOfTeams,
  );
  const [isLeadOfExperiences, setIsLeadOfExperiences] =
    useState<CallerTeamsState>(cachedLeadOfExperiences);

  useEffect(() => {
    if (attempted) return;
    if (!inFlight) {
      inFlight = api
        .whoAmI()
        .then(identity => {
          cachedTeams = identity.teams;
          cachedLeadOfTeams = identity.isLeadOfTeams ?? [];
          cachedLeadOfExperiences = identity.isLeadOfExperiences ?? [];
          return {
            teams: cachedTeams,
            isLeadOfTeams: cachedLeadOfTeams,
            isLeadOfExperiences: cachedLeadOfExperiences,
          };
        })
        .catch(error => {
          const state =
            error instanceof QuiesceServiceUnconfiguredError
              ? 'unconfigured'
              : null;
          cachedTeams = state;
          cachedLeadOfTeams = state;
          cachedLeadOfExperiences = state;
          return {
            teams: cachedTeams,
            isLeadOfTeams: cachedLeadOfTeams,
            isLeadOfExperiences: cachedLeadOfExperiences,
          };
        })
        .finally(() => {
          attempted = true;
          inFlight = null;
        });
    }
    inFlight.then(resolved => {
      setTeams(resolved.teams);
      setIsLeadOfTeams(resolved.isLeadOfTeams);
      setIsLeadOfExperiences(resolved.isLeadOfExperiences);
    });
  }, [api]);

  return { teams, isLeadOfTeams, isLeadOfExperiences };
}

/** Test-only: clears the module-level cache so each test starts fresh. */
export function _resetCallerIdentityCacheForTests(): void {
  cachedTeams = null;
  cachedLeadOfTeams = null;
  cachedLeadOfExperiences = null;
  attempted = false;
  inFlight = null;
}
